import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Package,
  Plus,
  MagnifyingGlass,
  PencilSimple,
  Trash,
  X,
  Check,
  Funnel,
  UploadSimple,
  Spinner
} from '@phosphor-icons/react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Toaster, toast } from 'sonner';
import { CurrencyInput } from '../../components/CurrencyInput';
import { PageHeader } from './components/PageHeader';
import { CATEGORIAS, UNIDADES, API, getCategoriaLabel } from './constants';

const emptyProduct = {
  categoria: '',
  sku: '',
  produto: '',
  marca: '',
  linha: '',
  volume: '',
  unidade: 'un',
  estoque_inicial: 0,
  estoque_minimo: 0,
  subcategoria: '',
  fornecedor: '',
  custo_unitario: 0,
  observacoes: ''
};

export const CadastroProdutos = () => {
  const navigate = useNavigate();
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategoria, setFilterCategoria] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [skuLoading, setSkuLoading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchProdutos = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/pacman/produtos`);
      setProdutos(response.data);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProdutos();
  }, [fetchProdutos]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      if (editingProduct) {
        await axios.put(`${API}/pacman/produtos/${editingProduct.id}`, formData);
        toast.success('Produto atualizado com sucesso!');
      } else {
        await axios.post(`${API}/pacman/produtos`, formData);
        toast.success('Produto cadastrado com sucesso!');
      }
      setIsDialogOpen(false);
      setEditingProduct(null);
      setFormData(emptyProduct);
      fetchProdutos();
    } catch (error) {
      toast.error('Erro ao salvar produto');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (produto) => {
    setEditingProduct(produto);
    setFormData({
      categoria: produto.categoria || '',
      sku: produto.sku || '',
      produto: produto.produto || '',
      marca: produto.marca || '',
      linha: produto.linha || '',
      volume: produto.volume || '',
      unidade: produto.unidade || 'un',
      estoque_inicial: produto.estoque_inicial || 0,
      estoque_minimo: produto.estoque_minimo || 0,
      subcategoria: produto.subcategoria || '',
      fornecedor: produto.fornecedor || '',
      custo_unitario: produto.custo_unitario || 0,
      observacoes: produto.observacoes || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este produto?')) return;
    
    try {
      await axios.delete(`${API}/pacman/produtos/${id}`);
      toast.success('Produto excluído com sucesso!');
      fetchProdutos();
    } catch (error) {
      toast.error('Erro ao excluir produto');
    }
  };

  const handleNewProduct = () => {
    setEditingProduct(null);
    setFormData(emptyProduct);
    setIsDialogOpen(true);
  };

  const handleCategoriaChange = async (categoria) => {
    setFormData(prev => ({...prev, categoria}));
    
    // Gera SKU automaticamente apenas para novo produto
    if (!editingProduct) {
      setSkuLoading(true);
      try {
        const res = await axios.get(`${API}/pacman/produtos/next-sku/${categoria}`);
        setFormData(prev => ({...prev, categoria, sku: res.data.sku}));
      } catch (err) {
      } finally {
        setSkuLoading(false);
      }
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.xlsx', '.csv'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExt)) {
      toast.error('Formato não suportado. Use arquivos .xlsx ou .csv');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/pacman/produtos/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const { inserted, skipped, errors } = response.data;
      
      if (inserted > 0) {
        toast.success(`${inserted} produto${inserted !== 1 ? 's' : ''} importado${inserted !== 1 ? 's' : ''} com sucesso!`);
      }
      if (skipped > 0) {
        toast.info(`${skipped} produto${skipped !== 1 ? 's' : ''} ignorado${skipped !== 1 ? 's' : ''} (SKU já existente)`);
      }
      if (errors && errors.length > 0) {
        toast.warning(`${errors.length} erro${errors.length !== 1 ? 's' : ''} durante a importação`);
      }

      fetchProdutos();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao importar planilha';
      toast.error(message);
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const filteredProdutos = produtos.filter(p => {
    const matchesSearch = 
      p.produto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.marca?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = filterCategoria === 'all' || p.categoria === filterCategoria;
    return matchesSearch && matchesCategoria;
  });

  return (
    <div className="min-h-screen bg-[#F7F6F4]" data-testid="cadastro-produtos-page">
      <Toaster position="top-right" richColors />
      <div className="salonup-container">
        <PageHeader 
          backTo="/gestao/pacman"
          backLabel="Voltar para PAC MAN"
          breadcrumbs={[
            { label: 'Home', path: '/' },
            { label: 'Gestão', path: '/gestao' },
            { label: 'PAC MAN', path: '/gestao/pacman' },
            { label: 'Cadastro de Produtos' }
          ]}
        />

        {/* Title Section */}
        <section className="mb-8 animate-fade-in-up opacity-0 delay-150" style={{ animationFillMode: 'forwards' }}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ backgroundColor: '#1B433212' }}>
                <Package size={28} weight="regular" style={{ color: '#1B4332' }} />
              </div>
              <div>
                <h1 className="salonup-heading-lg">Cadastro de Produtos</h1>
                <p className="salonup-text-sm mt-1">Base para registrar e organizar os itens do estoque</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".xlsx,.csv"
                className="hidden"
                data-testid="file-input"
              />
              
              {/* Import Button */}
              <Button 
                onClick={handleImportClick}
                disabled={importing}
                data-testid="btn-import"
                variant="outline"
                className="border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5 rounded-xl px-5 py-2.5 h-auto"
              >
                {importing ? (
                  <>
                    <Spinner size={20} className="mr-2 animate-spin" />
                    Importando...
                  </>
                ) : (
                  <>
                    <UploadSimple size={20} weight="bold" className="mr-2" />
                    Importar Planilha
                  </>
                )}
              </Button>
              
              {/* New Product Button */}
              <Button 
                onClick={handleNewProduct}
                data-testid="btn-new-product"
                className="bg-[#1B4332] hover:bg-[#143326] text-white rounded-xl px-5 py-2.5 h-auto"
              >
                <Plus size={20} weight="bold" className="mr-2" />
                Novo Produto
              </Button>
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="mb-6 animate-fade-in-up opacity-0 delay-200" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <MagnifyingGlass size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <Input 
                  placeholder="Buscar por nome, SKU ou marca..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 h-11 border-[#E4E4E7] rounded-xl"
                  data-testid="search-input"
                />
              </div>
              <div className="w-full md:w-64">
                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl" data-testid="filter-categoria">
                    <Funnel size={18} className="mr-2 text-[#A1A1AA]" />
                    <SelectValue placeholder="Filtrar por categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Products Table */}
        <section className="animate-fade-in-up opacity-0 delay-250" style={{ animationFillMode: 'forwards' }}>
          <div className="salonup-card overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <p className="salonup-text-body">Carregando produtos...</p>
              </div>
            ) : filteredProdutos.length === 0 ? (
              <div className="p-12 text-center">
                <Package size={48} weight="light" className="mx-auto mb-4 text-[#A1A1AA]" />
                <p className="salonup-heading-md mb-2">Nenhum produto encontrado</p>
                <p className="salonup-text-sm mb-6">
                  {searchTerm || filterCategoria !== 'all' 
                    ? 'Tente ajustar os filtros de busca'
                    : 'Comece cadastrando seu primeiro produto'}
                </p>
                {!searchTerm && filterCategoria === 'all' && (
                  <Button 
                    onClick={handleNewProduct}
                    className="bg-[#1B4332] hover:bg-[#143326] text-white rounded-xl"
                  >
                    <Plus size={20} weight="bold" className="mr-2" />
                    Cadastrar Produto
                  </Button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="products-table">
                  <thead className="bg-[#F7F6F4] border-b border-[#E4E4E7]">
                    <tr>
                      <th className="text-left p-4 salonup-label">SKU</th>
                      <th className="text-left p-4 salonup-label">Produto</th>
                      <th className="text-left p-4 salonup-label hidden md:table-cell">Categoria</th>
                      <th className="text-left p-4 salonup-label hidden lg:table-cell">Marca</th>
                      <th className="text-right p-4 salonup-label">Est. Inicial</th>
                      <th className="text-right p-4 salonup-label">Est. Mínimo</th>
                      <th className="text-right p-4 salonup-label hidden xl:table-cell">Último Custo</th>
                      <th className="text-center p-4 salonup-label">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProdutos.map((produto, index) => (
                      <tr 
                        key={produto.id} 
                        className="border-b border-[#E4E4E7] hover:bg-[#F7F6F4]/50 transition-colors"
                        data-testid={`product-row-${index}`}
                      >
                        <td className="p-4">
                          <span className="font-mono text-sm text-[#71717A]">{produto.sku || '-'}</span>
                        </td>
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-[#18181B]">{produto.produto}</p>
                            {produto.volume && (
                              <p className="text-xs text-[#71717A]">{produto.volume} {produto.unidade}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <span className="text-sm text-[#71717A]">{getCategoriaLabel(produto.categoria)}</span>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <span className="text-sm text-[#71717A]">{produto.marca || '-'}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="font-medium">{produto.estoque_inicial}</span>
                        </td>
                        <td className="p-4 text-right">
                          <span className="text-[#71717A]">{produto.estoque_minimo}</span>
                        </td>
                        <td className="p-4 text-right hidden xl:table-cell">
                          {produto.custo_unitario ? (
                            <span className="font-medium text-[#1B4332]">
                              R$ {produto.custo_unitario.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          ) : (
                            <span className="text-[#A1A1AA]">-</span>
                          )}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-1">
                            <button 
                              onClick={() => handleEdit(produto)}
                              className="p-2 hover:bg-[#E4E4E7] rounded-lg transition-colors"
                              data-testid={`btn-edit-${index}`}
                            >
                              <PencilSimple size={18} className="text-[#71717A]" />
                            </button>
                            <button 
                              onClick={() => handleDelete(produto.id)}
                              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                              data-testid={`btn-delete-${index}`}
                            >
                              <Trash size={18} className="text-red-500" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {filteredProdutos.length > 0 && (
            <p className="salonup-text-xs mt-4 text-center">
              {filteredProdutos.length} produto{filteredProdutos.length !== 1 ? 's' : ''} encontrado{filteredProdutos.length !== 1 ? 's' : ''}
            </p>
          )}
        </section>

        {/* Footer */}
        <footer className="salonup-footer animate-fade-in opacity-0" style={{ animationDelay: '500ms', animationFillMode: 'forwards' }}>
          <p className="salonup-text-xs">SalonUp — O sistema operacional do salão moderno</p>
        </footer>
      </div>

      {/* Product Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="salonup-heading-md">
              {editingProduct ? 'Editar Produto' : 'Novo Produto'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Categoria */}
              <div className="md:col-span-2">
                <label className="salonup-label mb-2 block">Categoria / Guia *</label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={handleCategoriaChange}
                  required
                >
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl">
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* SKU */}
              <div>
                <label className="salonup-label mb-2 block">
                  SKU
                  {!editingProduct && formData.categoria && (
                    <span className="text-[#52796F] font-normal ml-1 text-[10px]">
                      (gerado automaticamente)
                    </span>
                  )}
                </label>
                <div className="relative">
                  <Input 
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    placeholder={formData.categoria ? 'Gerando...' : 'Selecione a categoria primeiro'}
                    className={`h-11 border-[#E4E4E7] rounded-xl ${formData.sku && !editingProduct ? 'font-mono font-semibold text-[#1B4332]' : ''}`}
                    data-testid="input-sku"
                  />
                  {skuLoading && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Spinner size={16} className="animate-spin text-[#52796F]" />
                    </div>
                  )}
                </div>
              </div>

              {/* Produto */}
              <div>
                <label className="salonup-label mb-2 block">Nome do Produto *</label>
                <Input 
                  value={formData.produto}
                  onChange={(e) => setFormData({...formData, produto: e.target.value})}
                  placeholder="Ex: Shampoo Profissional"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                  required
                />
              </div>

              {/* Marca */}
              <div>
                <label className="salonup-label mb-2 block">Marca</label>
                <Input 
                  value={formData.marca}
                  onChange={(e) => setFormData({...formData, marca: e.target.value})}
                  placeholder="Ex: L'Oréal"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Linha */}
              <div>
                <label className="salonup-label mb-2 block">Linha</label>
                <Input 
                  value={formData.linha}
                  onChange={(e) => setFormData({...formData, linha: e.target.value})}
                  placeholder="Ex: Expert"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Volume */}
              <div>
                <label className="salonup-label mb-2 block">Volume / Quantidade</label>
                <Input 
                  value={formData.volume}
                  onChange={(e) => setFormData({...formData, volume: e.target.value})}
                  placeholder="Ex: 500"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Unidade */}
              <div>
                <label className="salonup-label mb-2 block">Unidade</label>
                <Select 
                  value={formData.unidade} 
                  onValueChange={(value) => setFormData({...formData, unidade: value})}
                >
                  <SelectTrigger className="h-11 border-[#E4E4E7] rounded-xl">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    {UNIDADES.map(un => (
                      <SelectItem key={un.value} value={un.value}>{un.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Estoque Inicial */}
              <div>
                <label className="salonup-label mb-2 block">Estoque Inicial</label>
                <Input 
                  type="number"
                  min="0"
                  value={formData.estoque_inicial}
                  onChange={(e) => setFormData({...formData, estoque_inicial: Number(e.target.value)})}
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Estoque Mínimo */}
              <div>
                <label className="salonup-label mb-2 block">Estoque Mínimo</label>
                <Input 
                  type="number"
                  min="0"
                  value={formData.estoque_minimo}
                  onChange={(e) => setFormData({...formData, estoque_minimo: Number(e.target.value)})}
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Subcategoria */}
              <div>
                <label className="salonup-label mb-2 block">Subcategoria</label>
                <Input 
                  value={formData.subcategoria}
                  onChange={(e) => setFormData({...formData, subcategoria: e.target.value})}
                  placeholder="Ex: Coloração"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Fornecedor */}
              <div>
                <label className="salonup-label mb-2 block">Fornecedor</label>
                <Input 
                  value={formData.fornecedor}
                  onChange={(e) => setFormData({...formData, fornecedor: e.target.value})}
                  placeholder="Ex: Distribuidora XYZ"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>

              {/* Último Custo */}
              <div>
                <label className="salonup-label mb-2 block">
                  Último Custo
                  <span className="text-[#A1A1AA] font-normal ml-1 text-[10px]">
                    (atualizado automaticamente nas entradas)
                  </span>
                </label>
                <CurrencyInput 
                  value={formData.custo_unitario}
                  onChange={(val) => setFormData({...formData, custo_unitario: val})}
                  data-testid="input-ultimo-custo"
                />
              </div>

              {/* Observações */}
              <div className="md:col-span-2">
                <label className="salonup-label mb-2 block">Observações</label>
                <Input 
                  value={formData.observacoes}
                  onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                  placeholder="Observações adicionais sobre o produto"
                  className="h-11 border-[#E4E4E7] rounded-xl"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-[#E4E4E7]">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsDialogOpen(false)}
                className="rounded-xl px-5"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-[#1B4332] hover:bg-[#143326] text-white rounded-xl px-5"
              >
                {saving ? 'Salvando...' : editingProduct ? 'Salvar Alterações' : 'Cadastrar Produto'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CadastroProdutos;
